import type { Material, Texture, WebGLRenderer } from 'three'
import {
  AmbientLight,
  BufferGeometry,
  Color,
  DirectionalLight,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Scene,
  Vector4,
  WebGLRenderTarget,
} from 'three'
import type { MeshClipPacket } from '@/plugins/mesh/clipping/MeshClipPacket'
import {
  createMeshClipMaterialVariant,
  MeshClipUniformBinding,
} from '@/plugins/mesh/clipping/MeshClipShader'
import { estimateThreeTextureBytes } from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import { classifyMeshMaterial } from '@/plugins/mesh/MeshPrimitiveNormalizer'
import { enumerateMaterialTextures } from '@/plugins/mesh/MeshResourceTracker'
import type {
  ITilePreparation,
  TileContentPackage,
  TileContentPrepareContext,
  TilePreparationVersion,
  TileRenderItem,
} from '@/plugins/tiles/contracts'
import type { MeshTileContentPackage } from '@/plugins/tiles/mesh/meshContentResources'
import { TilesError } from '@/plugins/tiles/TilesError'
import type { TilesAppearance } from '@/plugins/tiles/types'
import { createMeshAppearanceMaterial } from '@/shared/materials/createMeshAppearanceMaterial'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { ViewFrameContext } from '@/shared/types/view'

export interface TilesRenderBridgeOptions {
  /** 借用环境，桥接和内容销毁均不 dispose 此纹理。 */
  readonly environment?: Texture | null
  /** 默认在独立场景加入环境光和方向光，PBR 不依赖 mainScene 灯光。 */
  readonly defaultLights?: boolean
}

interface RenderPart {
  readonly source: Mesh
  readonly proxy: Mesh
  readonly sourceMaterial: Material
  readonly ownedGeometry: boolean
  appearanceMaterial?: Material
}

interface RenderEntry {
  readonly content: TileContentPackage
  readonly assetId: string
  readonly parts: readonly RenderPart[]
  readonly clipMaterials: Map<Material, Material>
  unsubscribe?: () => void
}

function createScene(options: TilesRenderBridgeOptions): Scene {
  const scene = new Scene()
  scene.environment = options.environment ?? null
  if (options.defaultLights !== false) {
    scene.add(new AmbientLight(0xffffff, 1.5))
    const sun = new DirectionalLight(0xffffff, 2.5)
    sun.position.set(3, -4, 8)
    scene.add(sun)
  }
  return scene
}

function createProxy(source: Mesh, material: Material, start?: number, count?: number): Mesh {
  let geometry = source.geometry
  if (start !== undefined && count !== undefined) {
    geometry = new BufferGeometry()
    geometry.name = source.geometry.name
    geometry.setIndex(source.geometry.getIndex())
    for (const [name, attribute] of Object.entries(source.geometry.attributes))
      geometry.setAttribute(name, attribute)
    geometry.boundingBox = source.geometry.boundingBox?.clone() ?? null
    geometry.boundingSphere = source.geometry.boundingSphere?.clone() ?? null
    const first = Math.max(start, source.geometry.drawRange.start)
    const available =
      source.geometry.index?.count ?? source.geometry.getAttribute('position')?.count ?? 0
    const last = Math.min(
      start + count,
      source.geometry.drawRange.start + source.geometry.drawRange.count,
      available
    )
    geometry.setDrawRange(first, Math.max(0, last - first))
  }
  const proxy = new Mesh(geometry, material)
  proxy.name = source.name
  proxy.matrixAutoUpdate = false
  proxy.matrixWorldAutoUpdate = false
  proxy.castShadow = source.castShadow
  proxy.receiveShadow = source.receiveShadow
  proxy.frustumCulled = source.frustumCulled
  proxy.renderOrder = source.renderOrder
  proxy.layers.mask = source.layers.mask
  proxy.userData = { ...source.userData }
  return proxy
}

function createParts(content: TileContentPackage): RenderPart[] {
  const parts: RenderPart[] = []
  content.object3D.traverse((object) => {
    if (!(object instanceof Mesh)) return
    const add = (material: Material, start?: number, count?: number) => {
      if (count !== undefined && count <= 0) return
      parts.push({
        source: object,
        proxy: createProxy(object, material, start, count),
        sourceMaterial: material,
        ownedGeometry: start !== undefined,
      })
    }
    if (Array.isArray(object.material)) {
      for (const group of object.geometry.groups) {
        const material = object.material[group.materialIndex ?? 0]
        if (material) add(material, group.start, group.count)
      }
    } else add(object.material)
  })
  return parts
}

function releaseParts(parts: readonly RenderPart[]): void {
  const errors: unknown[] = []
  for (const part of parts) {
    part.proxy.removeFromParent()
    if (part.ownedGeometry) {
      try {
        // Three 会随 geometry.dispose 删除属性缓冲区；这些属性属于共享源几何。
        part.proxy.geometry.setIndex(null)
        for (const name of Object.keys(part.proxy.geometry.attributes))
          part.proxy.geometry.deleteAttribute(name)
        part.proxy.geometry.dispose()
      } catch (error) {
        errors.push(error)
      }
    }
  }
  if (errors.length) throw new AggregateError(errors, 'Tiles 绘制代理清理失败')
}

function isVisible(source: Mesh, content: TileContentPackage): boolean {
  let object: import('three').Object3D | null = source
  while (object) {
    if (!object.visible) return false
    if (object === content.object3D) return true
    object = object.parent
  }
  return false
}

function rendererState(renderer: WebGLRenderer) {
  const target = renderer.getRenderTarget()
  const face = renderer.getActiveCubeFace()
  const level = renderer.getActiveMipmapLevel()
  const viewport = renderer.getViewport(new Vector4())
  const scissor = renderer.getScissor(new Vector4())
  const scissorTest = renderer.getScissorTest()
  const autoClear = renderer.autoClear
  return () => {
    renderer.setRenderTarget(target, face, level)
    renderer.setViewport(viewport)
    renderer.setScissor(scissor)
    renderer.setScissorTest(scissorTest)
    renderer.autoClear = autoClear
  }
}

/** 分组代理借用源资源并独立管理派生材质，从不迁移或改写内容节点。 */
export class TilesRenderBridge {
  private readonly _opaque: Scene
  private readonly _transparent: Scene
  private readonly _entries = new Map<TileContentPackage, Map<string, RenderEntry>>()
  private readonly _appearances = new Map<string, TilesAppearance>()
  private _disposed = false
  private readonly _clipBinding = new MeshClipUniformBinding()

  constructor(private readonly _options: TilesRenderBridgeOptions = {}) {
    this._opaque = createScene(_options)
    this._transparent = createScene(_options)
  }

  /** 按 Record 设置外观；恢复源外观时释放其派生资源，新瓦片在首次绘制时继承。 */
  setAppearance(assetId: string, appearance?: TilesAppearance): void {
    this.assertActive()
    if (
      appearance &&
      (!['source', 'solidColor'].includes(appearance.mode) || !Number.isFinite(appearance.opacity))
    ) {
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        assetId,
        message: 'Tiles 外观需要有效模式和有限不透明度',
      })
    }
    if (appearance) {
      const snapshot = { ...appearance, color: new Color(appearance.color) }
      this._appearances.set(assetId, snapshot)
      for (const entries of this._entries.values()) {
        const entry = entries.get(assetId)
        if (entry) this.applyAppearance(entry, snapshot)
      }
    } else {
      this._appearances.delete(assetId)
      for (const entries of this._entries.values()) {
        const entry = entries.get(assetId)
        if (entry) this.releaseEntry(entry)
      }
    }
  }

  /** 纹理上传和 shader 编译可先于显示提交；静态 attribute 在首绘同步上传。 */
  async prepare(content: TileContentPackage, context: TileContentPrepareContext): Promise<void> {
    const task = this.createPreparation(content, context, { renderer: 0, content: 0, quality: 0 })
    try {
      for (;;) {
        const step = task.step()
        if (step.state === 'ready') return
        if (step.state === 'waiting') await step.completion
      }
    } finally {
      task.cancel()
    }
  }

  createPreparation(
    content: TileContentPackage,
    context: TileContentPrepareContext,
    version: TilePreparationVersion
  ): ITilePreparation {
    this.assertActive()
    if ((content as Partial<MeshTileContentPackage>).disposed)
      throw new TilesError({ code: 'disposed', stage: 'runtime', message: '内容已释放' })
    if (context.graphics.kind !== 'webgl2') {
      let cancelled = false
      let initialized = false
      return {
        version,
        mode: 'legacy',
        nextStep: 'ready',
        step: () => {
          this.assertActive()
          if (cancelled || (content as Partial<MeshTileContentPackage>).disposed)
            throw new TilesError({ code: 'cancelled', stage: 'runtime', message: '准备任务已取消' })
          if (!initialized) {
            content.object3D.updateMatrixWorld(true)
            initialized = true
          }
          return { state: 'ready', uploadedBytes: 0 }
        },
        cancel: () => {
          cancelled = true
        },
      }
    }
    const renderer = context.legacyRenderer
    if (!renderer)
      throw new TilesError({
        code: 'unsupported-capability',
        stage: 'runtime',
        message: 'Tiles Mesh WebGL2 准备缺少 legacy renderer',
      })
    const scene = createScene(this._options)
    let parts: RenderPart[] = []
    let cancelled = false
    let waiting = false
    let released = false
    let initialized = false
    let compiled = false
    let index = 0
    let drawIndex = 0
    let target: WebGLRenderTarget | undefined
    const uploadedGeometry = new Set<BufferGeometry>()
    const textures = new Set<Texture>()
    let uploads: Texture[] = []
    const release = () => {
      if (released || waiting) return
      released = true
      try {
        releaseParts(parts)
      } finally {
        target?.dispose()
      }
    }
    const initialize = () => {
      parts = createParts(content)
      content.object3D.updateMatrixWorld(true)
      for (const part of parts) {
        part.proxy.matrix.copy(part.source.matrixWorld)
        part.proxy.matrixWorld.copy(part.source.matrixWorld)
        scene.add(part.proxy)
        for (const texture of enumerateMaterialTextures(part.proxy.material as Material))
          textures.add(texture)
      }
      uploads = [...textures]
      initialized = true
    }
    return {
      version,
      mode: 'incremental',
      get nextStep() {
        if (!initialized || index < uploads.length) return 'upload' as const
        if (!compiled) return 'compile' as const
        return drawIndex < parts.length ? ('first-draw' as const) : ('ready' as const)
      },
      step: () => {
        this.assertActive()
        if (cancelled || (content as Partial<MeshTileContentPackage>).disposed)
          throw new TilesError({ code: 'cancelled', stage: 'runtime', message: '准备任务已取消' })
        if (!initialized) initialize()
        if (compiled && drawIndex >= parts.length) return { state: 'ready' }
        const restore = rendererState(renderer)
        try {
          if (compiled) {
            // 用公开 renderer 路径提前完成一个 primitive 的首绘；不写 Three 私有缓冲缓存。
            if (drawIndex === 0) for (const part of parts) part.proxy.removeFromParent()
            const part = parts[drawIndex++]
            target ??= new WebGLRenderTarget(1, 1)
            scene.add(part.proxy)
            part.proxy.frustumCulled = false
            renderer.setRenderTarget(target)
            renderer.setViewport(0, 0, 1, 1)
            renderer.setScissorTest(false)
            renderer.autoClear = true
            try {
              renderer.render(scene, new PerspectiveCamera())
            } finally {
              part.proxy.removeFromParent()
            }
            let bytes = 0
            if (!uploadedGeometry.has(part.source.geometry)) {
              uploadedGeometry.add(part.source.geometry)
              for (const attribute of [
                ...Object.values(part.source.geometry.attributes),
                part.source.geometry.index,
              ])
                if (attribute)
                  bytes += ('data' in attribute ? attribute.data.array : attribute.array).byteLength
            }
            return {
              state: drawIndex >= parts.length ? 'ready' : 'pending',
              uploadedBytes: bytes,
            }
          }
          if (index < uploads.length) {
            const texture = uploads[index++]
            renderer.initTexture(texture)
            return { state: 'pending', uploadedBytes: estimateThreeTextureBytes(texture) }
          }
          const camera = new PerspectiveCamera()
          if (typeof renderer.compileAsync === 'function') {
            const compilation = renderer.compileAsync(scene, camera)
            waiting = true
            const completion = Promise.resolve(compilation)
              .then(() => {
                compiled = true
              })
              .finally(() => {
                waiting = false
                if (cancelled) release()
              })
            return { state: 'waiting', completion }
          }
          renderer.compile(scene, camera)
          compiled = true
          return { state: 'pending', uploadedBytes: 0 }
        } finally {
          restore()
        }
      },
      cancel: (force = false) => {
        cancelled = true
        if (force) waiting = false
        release()
      },
    }
  }

  render(
    items: readonly TileRenderItem[],
    frame: ViewFrameContext,
    phase: RenderPhase,
    context: PhaseRenderContext,
    clipPacket: MeshClipPacket | null = null
  ): void {
    this.assertActive()
    if (phase !== RenderPhase.MainOpaque && phase !== RenderPhase.MainTransparent) return
    // 一个适配器可被多个 Record 共用，单次 items 并不是适配器的全部使用者。
    // 正式包在最终租约退出时通过 onDispose 释放代理；通用包由 bridge.dispose 兜底。
    const scene = phase === RenderPhase.MainOpaque ? this._opaque : this._transparent
    const attached: Mesh[] = []
    const originalMaterials = new Map<Mesh, Material>()
    const matrix = new Matrix4()
    try {
      for (const item of items) {
        if ((item.content as Partial<MeshTileContentPackage>).disposed) continue
        const assetId = item.assetId ?? item.content.source.assetId
        let entries = this._entries.get(item.content)
        if (!entries) {
          entries = new Map()
          this._entries.set(item.content, entries)
        }
        let entry = entries.get(assetId)
        if (!entry) {
          entry = {
            content: item.content,
            assetId,
            parts: createParts(item.content),
            clipMaterials: new Map(),
          }
          entries.set(assetId, entry)
          const appearance = this._appearances.get(assetId)
          if (appearance) this.applyAppearance(entry, appearance)
          const current = entry
          entry.unsubscribe = (item.content as Partial<MeshTileContentPackage>).onDispose?.(() =>
            this.releaseEntry(current)
          )
        }
        item.content.object3D.updateMatrixWorld(true)
        for (const part of entry.parts) {
          const material = part.proxy.material as Material
          const partPhase =
            classifyMeshMaterial(material) === 'blend'
              ? RenderPhase.MainTransparent
              : RenderPhase.MainOpaque
          if (partPhase !== phase || !material.visible || !isVisible(part.source, item.content))
            continue
          if (clipPacket) {
            const source = part.proxy.material as Material
            let variant = entry.clipMaterials.get(source)
            if (!variant) {
              variant = createMeshClipMaterialVariant(source, this._clipBinding, () => {})?.material
              if (variant) entry.clipMaterials.set(source, variant)
            }
            if (variant) {
              originalMaterials.set(part.proxy, source)
              part.proxy.material = variant
            }
          }
          matrix.multiplyMatrices(item.matrixWorld, part.source.matrixWorld)
          part.proxy.matrix.copy(matrix)
          part.proxy.matrixWorld.copy(matrix)
          scene.add(part.proxy)
          attached.push(part.proxy)
        }
      }
      if (!attached.length) return
      if (context.graphics.kind === 'webgpu') {
        if (!context.graphics.renderScene) {
          throw new TilesError({
            code: 'unsupported-capability',
            stage: 'runtime',
            message: '当前 WebGPU backend 不支持 renderScene 提交',
          })
        }
        this._clipBinding.bind(clipPacket)
        context.graphics.renderScene(scene, frame.camera)
        return
      }
      const renderer = context.renderer
      const restore = rendererState(renderer)
      try {
        this._clipBinding.bind(clipPacket)
        renderer.autoClear = false
        renderer.setRenderTarget(context.outputTarget)
        renderer.setViewport(
          context.viewport.x,
          context.viewport.y,
          context.viewport.width,
          context.viewport.height
        )
        renderer.setScissor(
          context.viewport.x,
          context.viewport.y,
          context.viewport.width,
          context.viewport.height
        )
        renderer.setScissorTest(true)
        renderer.render(scene, frame.camera)
      } finally {
        restore()
      }
    } finally {
      this._clipBinding.clear()
      for (const [proxy, material] of originalMaterials) proxy.material = material
      for (const proxy of attached) proxy.removeFromParent()
    }
  }

  dispose(): void {
    if (this._disposed) return
    this._disposed = true
    const errors: unknown[] = []
    for (const entries of this._entries.values()) {
      for (const entry of entries.values()) {
        try {
          this.releaseEntry(entry)
        } catch (error) {
          errors.push(error)
        }
      }
    }
    this._appearances.clear()
    this._opaque.clear()
    this._transparent.clear()
    this._opaque.environment = null
    this._transparent.environment = null
    if (errors.length) throw new AggregateError(errors, 'Tiles Mesh 桥接清理失败')
  }

  invalidateContent(content: TileContentPackage): void {
    for (const entry of [...(this._entries.get(content)?.values() ?? [])]) this.releaseEntry(entry)
  }

  private releaseAppearance(entry: RenderEntry): void {
    const errors: unknown[] = []
    for (const material of entry.clipMaterials.values()) {
      try {
        material.dispose()
      } catch (error) {
        errors.push(error)
      }
    }
    entry.clipMaterials.clear()
    for (const part of entry.parts) {
      part.proxy.material = part.sourceMaterial
      try {
        part.appearanceMaterial?.dispose()
      } catch (error) {
        errors.push(error)
      }
      part.appearanceMaterial = undefined
    }
    if (errors.length) throw new AggregateError(errors, 'Tiles 派生材质清理失败')
  }

  private applyAppearance(entry: RenderEntry, appearance: TilesAppearance): void {
    this.releaseAppearance(entry)
    for (const part of entry.parts) {
      part.appearanceMaterial = createMeshAppearanceMaterial(
        part.proxy.geometry,
        part.sourceMaterial,
        appearance
      )
      part.proxy.material = part.appearanceMaterial
    }
  }

  private releaseEntry(entry: RenderEntry): void {
    const entries = this._entries.get(entry.content)
    entries?.delete(entry.assetId)
    if (!entries?.size) this._entries.delete(entry.content)
    entry.unsubscribe?.()
    const errors: unknown[] = []
    try {
      this.releaseAppearance(entry)
    } catch (error) {
      errors.push(error)
    }
    try {
      releaseParts(entry.parts)
    } catch (error) {
      errors.push(error)
    }
    if (errors.length) throw new AggregateError(errors, 'Tiles 裁切代理清理失败')
  }

  private assertActive(): void {
    if (this._disposed)
      throw new TilesError({ code: 'disposed', stage: 'runtime', message: 'Tiles Mesh 桥接已销毁' })
  }
}
